// src/HomePage/Game/FolkGame/OAnQuan/OAnQuanLogic.js

export const createInitialOAnQuanBoard = () => {
  return [
    5, 5, 5, 5, 5,   // 0 - 4: Dân P1
    10,              // 5: Quan Đông (Phải)
    5, 5, 5, 5, 5,   // 6 - 10: Dân P2
    10               // 11: Quan Tây (Trái)
  ];
};

export const normalizeOAnQuanBoard = (rawBoard) => {
  if (!rawBoard) return createInitialOAnQuanBoard();
  if (Array.isArray(rawBoard) && rawBoard.length === 12) return rawBoard;
  const board = [];
  for (let i = 0; i < 12; i++) {
    board.push(rawBoard[i] !== undefined ? Number(rawBoard[i]) : 0);
  }
  return board;
};

export const isQuanSquare = (idx) => idx === 5 || idx === 11;
export const isPlayer1Square = (idx) => idx >= 0 && idx <= 4;
export const isPlayer2Square = (idx) => idx >= 6 && idx <= 10;

/**
 * Thuật toán rải quân & ăn sỏi chuẩn luật dân gian Việt Nam
 * Trả về chuỗi sự kiện stepEvents phục vụ Animation từng bước
 */
export const executeOAnQuanMove = (board, startIndex, direction, isFirstTurnOfGame = false) => {
  const newBoard = [...board];
  const stepEvents = [];

  let stonesInHand = newBoard[startIndex];
  newBoard[startIndex] = 0;

  stepEvents.push({
    type: 'PICK',
    index: startIndex,
    stonesInHand,
    boardState: [...newBoard]
  });

  let currentIdx = startIndex;
  const step = direction === 'cw' ? 1 : -1;
  let eatenPoints = 0;

  while (stonesInHand > 0) {
    currentIdx = (currentIdx + step + 12) % 12;
    newBoard[currentIdx] += 1;
    stonesInHand -= 1;

    stepEvents.push({
      type: 'DROP',
      index: currentIdx,
      stonesInHand,
      boardState: [...newBoard]
    });

    // Khi rải hết sỏi trên tay
    if (stonesInHand === 0) {
      const nextIdx = (currentIdx + step + 12) % 12;

      // 1. Ô tiếp theo là Ô QUAN -> Chững lại dừng lượt, không được bốc Quan đi tiếp
      if (isQuanSquare(nextIdx)) {
        stepEvents.push({ type: 'STOP_AT_QUAN', index: nextIdx, boardState: [...newBoard] });
        break;
      }

      // 2. Ô tiếp theo là Ô DÂN CÓ SỎI -> Bốc tiếp để rải
      if (newBoard[nextIdx] > 0) {
        stonesInHand = newBoard[nextIdx];
        newBoard[nextIdx] = 0;
        currentIdx = nextIdx;

        stepEvents.push({
          type: 'PICK_AGAIN',
          index: nextIdx,
          stonesInHand,
          boardState: [...newBoard]
        });
      }
      // 3. Ô tiếp theo TRỐNG -> Xử lý Ăn Quân Liên Hoàn
      else if (newBoard[nextIdx] === 0) {
        let checkEmptyIdx = nextIdx;

        while (newBoard[checkEmptyIdx] === 0) {
          // LUẬT: Không đập Quan (Nếu ô trống là ô Quan -> Dừng ngay, không ăn qua đầu Quan)
          if (isQuanSquare(checkEmptyIdx)) {
            stepEvents.push({ type: 'CANNOT_EAT_OVER_QUAN', index: checkEmptyIdx, boardState: [...newBoard] });
            break;
          }

          const targetEatIdx = (checkEmptyIdx + step + 12) % 12;

          // Nếu ô sau ô trống là Ô Quan
          if (isQuanSquare(targetEatIdx)) {
            // LUẬT: Không ăn ô Quan ở lượt đầu tiên (Quan Non)
            if (isFirstTurnOfGame) {
              stepEvents.push({ type: 'SKIP_QUAN_NON', index: targetEatIdx, boardState: [...newBoard] });
              break;
            }

            if (newBoard[targetEatIdx] > 0) {
              const points = newBoard[targetEatIdx];
              eatenPoints += points;
              newBoard[targetEatIdx] = 0;

              stepEvents.push({
                type: 'EAT',
                index: targetEatIdx,
                eatenAmount: points,
                boardState: [...newBoard]
              });
              break; // Sau khi ăn Quan thì dừng lượt
            } else {
              break;
            }
          }

          // Nếu ô sau ô trống là Ô Dân có sỏi -> Ăn sỏi
          if (newBoard[targetEatIdx] > 0) {
            const points = newBoard[targetEatIdx];
            eatenPoints += points;
            newBoard[targetEatIdx] = 0;

            stepEvents.push({
              type: 'EAT',
              index: targetEatIdx,
              eatenAmount: points,
              boardState: [...newBoard]
            });

            // Tiếp tục kiểm tra ăn chùm liên hoàn
            checkEmptyIdx = (targetEatIdx + step + 12) % 12;
          } else {
            // Gặp 2 ô trống liên tiếp -> Dừng lượt
            stepEvents.push({ type: 'STOP_DOUBLE_EMPTY', index: targetEatIdx, boardState: [...newBoard] });
            break;
          }
        }
        break;
      }
    }
  }

  return { newBoard, eatenPoints, stepEvents };
};

export const checkAndApplyRefill = (board, isPlayer1, currentScore) => {
  const range = isPlayer1 ? [0, 1, 2, 3, 4] : [6, 7, 8, 9, 10];
  const hasStones = range.some((idx) => board[idx] > 0);

  if (!hasStones) {
    const updatedBoard = [...board];
    range.forEach((idx) => {
      updatedBoard[idx] = 1;
    });
    return { updatedBoard, newScore: currentScore - 5, refilled: true };
  }

  return { updatedBoard: board, newScore: currentScore, refilled: false };
};

export const checkOAnQuanFinished = (board) => {
  return board[5] === 0 && board[11] === 0;
};

export const calculateFinalScores = (board, p1Score, p2Score) => {
  const p1Remaining = [0, 1, 2, 3, 4].reduce((sum, idx) => sum + (board[idx] || 0), 0);
  const p2Remaining = [6, 7, 8, 9, 10].reduce((sum, idx) => sum + (board[idx] || 0), 0);
  return {
    finalP1Score: p1Score + p1Remaining,
    finalP2Score: p2Score + p2Remaining
  };
};

export const getBotOAnQuanMove = (board, difficulty = 'hard', isFirstTurnOfGame = false) => {
  const validIndices = [6, 7, 8, 9, 10].filter((idx) => board[idx] > 0);
  if (validIndices.length === 0) return null;

  const allPossibleMoves = [];
  for (const idx of validIndices) {
    for (const dir of ['cw', 'ccw']) {
      const { newBoard, eatenPoints } = executeOAnQuanMove(board, idx, dir, isFirstTurnOfGame);
      allPossibleMoves.push({
        idx,
        direction: dir,
        eatenPoints,
        resultBoard: newBoard
      });
    }
  }

  if (difficulty === 'easy') {
    if (Math.random() < 0.45) {
      const randomMove = allPossibleMoves[Math.floor(Math.random() * allPossibleMoves.length)];
      return { idx: randomMove.idx, direction: randomMove.direction };
    }
  }

  if (difficulty === 'medium') {
    allPossibleMoves.sort((a, b) => b.eatenPoints - a.eatenPoints);
    return { idx: allPossibleMoves[0].idx, direction: allPossibleMoves[0].direction };
  }

  const scoredMoves = allPossibleMoves.map((move) => {
    let score = move.eatenPoints * 2.2;
    let maxOpponentEat = 0;
    const oppIndices = [0, 1, 2, 3, 4].filter((i) => move.resultBoard[i] > 0);
    for (const oppIdx of oppIndices) {
      for (const oppDir of ['cw', 'ccw']) {
        const sim = executeOAnQuanMove(move.resultBoard, oppIdx, oppDir, false);
        if (sim.eatenPoints > maxOpponentEat) maxOpponentEat = sim.eatenPoints;
      }
    }
    score -= maxOpponentEat * 1.6;
    return { ...move, totalScore: score };
  });

  scoredMoves.sort((a, b) => b.totalScore - a.totalScore);
  return { idx: scoredMoves[0].idx, direction: scoredMoves[0].direction };
};